import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { reset, getDocuments } from '../../redux/actions/SignerAction';
import Loading from '../Loader/Loader';
import ErrorPage from '../Error/ErrorPage';
import '../../assets/style/SignDocument.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const SignDocument = (props) => {
  const [base64String, setBase64String] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [genericError, setGenericError] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [encodedString, setEncodedString] = useState("");
  const [isContentUnderstood, setIsContentUnderstood] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [currentPage, setCurrentPage] = useState(1);

  const documentViewerRef = useRef(null);
  const {
    reset,
    getDocuments,
    document_Details,
    fetching,
    error,
    t
  } = props;

  useEffect(() => {
    reset();

    const query = new URLSearchParams(window.location.search);
    const Id = query.get('id');

    if (!Id) {
      setGenericError(true);
      return;
    }

    setEncodedString(Id);
    getDocuments(Id);

  }, [reset, getDocuments]);


  useEffect(() => {
    if (document_Details) {
      setBase64String(document_Details.DocumentString);
      setAlreadySigned(document_Details.IsSigned);
      setIsCancelled(document_Details.IsCancelled);
    }
  }, [document_Details]);


  useEffect(() => {
    const viewer = documentViewerRef.current;
    return () => {
      if (viewer) {
        viewer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    if (documentViewerRef.current) {
      documentViewerRef.current.addEventListener('scroll', handleScroll);
    }
  };

  const handleScroll = () => {
    const viewer = documentViewerRef.current;
    if (!viewer) return;

    const pages = viewer.querySelectorAll('.react-pdf__Page');
    const scrollTop = viewer.scrollTop;
    const viewportHeight = viewer.clientHeight;
    const viewportCenter = scrollTop + viewportHeight / 2;

    let currentVisiblePage = 1;

    pages.forEach((page, index) => {
      const pageTop = page.offsetTop;
      const pageBottom = pageTop + page.clientHeight;

      if (viewportCenter >= pageTop && viewportCenter <= pageBottom) {
        currentVisiblePage = index + 1;
      }
    });

    setCurrentPage(currentVisiblePage);
  };

  const handleCheckboxChange = (e) => {
    setIsContentUnderstood(e.target.checked);
  };

  const handleStartSigning = () => {
    if (isContentUnderstood) {
      window.location = `/authentication?id=${encodedString}`;
    }
  };

  const handleDownloadPDF = () => {
    const linkSource = `data:application/pdf;base64,${base64String}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = "document.pdf";
    downloadLink.click();
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  return (
    <div className="sign-document-container">
      {fetching && !error && <Loading />}

      {error || genericError ? (
        <ErrorPage />
      ) : (
        <div>
          {!alreadySigned && !isCancelled && base64String ? (
            <div className="signing-interface">
              <div className="document-header">
                <div className="header-title">
                  <span className="header-icon">≡</span>
                  <h2>{t('emailsigning.signingtitle')}</h2>
                </div>
              </div>

              <div className="document-viewer" ref={documentViewerRef}>
                <div
                  className="pdf-container"
                  style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}
                >
                  <Document
                    file={`data:application/pdf;base64,${base64String}`}
                    onLoadSuccess={onDocumentLoadSuccess}
                  >
                    {Array.from(new Array(numPages), (_, i) => (
                      <Page
                        key={i + 1}
                        pageNumber={i + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={600}
                      />
                    ))}
                  </Document>
                </div>
              </div>

              <div className="document-controls">
                <div className="zoom-controls">
                  <button className="zoom-btn" onClick={handleZoomOut}>−</button>
                  <button className="zoom-btn" onClick={handleZoomIn}>+</button>
                </div>

                <div className="page-indicator">
                  {t('emailsigning.page')} {currentPage}/{numPages}
                </div>

                <button className="download-btn" onClick={handleDownloadPDF}>
                  <span className="download-icon">⬇</span> {t('emailsigning.downloadcopy')}
                </button>
              </div>

              <div className="signing-footer">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="contentUnderstood"
                    checked={isContentUnderstood}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="contentUnderstood">
                    {t('emailsigning.consentmsg')}
                  </label>
                </div>

                <button
                  className={`start-signing-btn ${!isContentUnderstood ? 'disabled' : ''}`}
                  onClick={handleStartSigning}
                  disabled={!isContentUnderstood}
                >
                  {t('emailsigning.startsigningmsg')}
                </button>
              </div>
            </div>
          ) : isCancelled ? (
            <div className="status-message error-message">
              <h1>{t('esign.isCancelledMsg')}</h1>
            </div>
          ) : alreadySigned ? (
            <div className="status-message error-message">
              <h1>{t('esign.alreadySignedMsg')}</h1>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  document_Details: state.sign.documentDetails,
  fetching: state.sign.fetching,
  error: state.error.isError,
});

export default connect(mapStateToProps, { getDocuments, reset })(
  withTranslation('common')(SignDocument)
);